import { useState } from "react";
import { Form, Formik } from "formik";
import { PRIMARY_ADDRESS_HI_COPY } from "pages/PreQualification/utils/constants";
import styles from "../PrimaryBusinessAddress.module.css";
import FooterButton from "uicomponents/FooterButton";
import BussinessAddressComponent from "uicomponents/AddressComponent/BusinessAddressComponent";
import Alert from "uicomponents/Alert";
import {
  BACK_BUTTON_TEXT,
  LOADING_BUTTON_TEXT,
  NEXT_BUTTON_TEXT,
} from "pages/PreQualification/utils/constants";
import { BusinessAddressFormType } from "uicomponents/AddressComponent/FormUtils";
import { BusinessAddressInitialFormValues } from "uicomponents/AddressComponent/FormUtils";
import { SingleBusinessAddressType } from "types";
import { businessAddressValidationSchema } from "uicomponents/AddressComponent/FormUtils";
import { errorMessageMap } from "utils/helper";
import { trackFormError } from "utils/analytics/trackEvent";
import { useUpdateBussinessAddressMutation } from "types/generated/operations";
import EditCard from "uicomponents/EditCard";
import SaveAddressList from "uicomponents/AddressComponent/SavedAddress";
import SuggestedAddressModel from "uicomponents/SuggestedAddressModel";
import InvalidDialog from "./InvalidDialog";

interface IPrimaryAddressEditFormProps {
  data?: SingleBusinessAddressType;
  handleNextClick: () => void;
  disableNextStep?: boolean;
}

const PrimaryAddressEditForm = ({
  ...props
}: IPrimaryAddressEditFormProps): JSX.Element => {
  const { data, handleNextClick } = props;

  const [suggestedAddresses, setSuggestedAddresses] = useState<
    BusinessAddressFormType[]
  >([]);

  const initialFormValues: BusinessAddressFormType =
    BusinessAddressInitialFormValues(data);

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [invalidWarning, setInvalidWarning] = useState(false);

  const [updateOfficeLocation, { loading: updateBussinessAddressLoading }] =
    useUpdateBussinessAddressMutation();

  const handleOnSubmit = async (values: BusinessAddressFormType) => {
    try {
      const response = await updateOfficeLocation({
        variables: {
          uuid: values.uuid,
          address1: values.address1,
          address2: values.address2,
          city: values.city,
          zip: values.zip,
          state: values.state,
          locationName: values.locationName,
          isHq: true,
          subdivision: values?.subdivision,
        },
      });

      if (response?.data?.updateOfficeLocation?.autoValidated === false) {
        if (response?.data?.updateOfficeLocation?.suggestedAddress) {
          setSuggestedAddresses([
            {
              locationName: values.locationName,
              address1: values.address1,
              address2: values.address2,
              city: values.city,
              zip: values.zip,
              state: values.state,
              uuid: values.uuid,
              subdivision: values.subdivision,
            },
            {
              locationName: values.locationName,
              address1:
                response?.data?.updateOfficeLocation?.suggestedAddress
                  ?.address1,
              address2:
                response?.data?.updateOfficeLocation?.suggestedAddress
                  ?.address2 || "",
              city: response?.data?.updateOfficeLocation?.suggestedAddress
                ?.city,
              zip: response?.data?.updateOfficeLocation?.suggestedAddress?.zip,
              state:
                response?.data?.updateOfficeLocation?.suggestedAddress?.state,
              uuid: values.uuid,
              subdivision: values.subdivision,
            },
          ]);
        } else {
          setSuggestedAddresses([]);
          setInvalidWarning(true);
        }
        return;
      }
      handleNextClick();
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, errorMessage);
      setIsError(true);
      return;
    }
  };

  const handleKeepEntered = () => {
    setSuggestedAddresses([]);
    handleNextClick();
  };

  return (
    <>
      <InvalidDialog
        isOpen={invalidWarning}
        onContinue={() => {
          setInvalidWarning(false);
          handleNextClick();
        }}
        onReview={() => setInvalidWarning(false)}
      />

      {suggestedAddresses.length > 0 && (
        <SuggestedAddressModel
          enteredAddress={suggestedAddresses[0]}
          suggestedAddress={suggestedAddresses[1]}
          onUseSuggestion={handleOnSubmit}
          onKeepCurrent={handleKeepEntered}
        />
      )}
      <div className={styles.title}>{PRIMARY_ADDRESS_HI_COPY.title}</div>
      <div className={styles.description}>
        {PRIMARY_ADDRESS_HI_COPY.description}
      </div>
      {isEditing && (
        <Formik
          initialValues={initialFormValues}
          validationSchema={businessAddressValidationSchema}
          onSubmit={handleOnSubmit}
        >
          {() => (
            <>
              <Form>
                <EditCard
                  title={PRIMARY_ADDRESS_HI_COPY.title}
                  showEditIcon={false}
                  description={PRIMARY_ADDRESS_HI_COPY.openCardDescription}
                >
                  <BussinessAddressComponent
                    hasMargin={true}
                    isPrimaryAddress={true}
                  />
                </EditCard>
                {isError && <Alert message={errorMessage} />}
                <FooterButton
                  nextButtonText={
                    updateBussinessAddressLoading
                      ? LOADING_BUTTON_TEXT
                      : NEXT_BUTTON_TEXT
                  }
                  backButtonText={BACK_BUTTON_TEXT}
                  disabled={updateBussinessAddressLoading}
                />
              </Form>
            </>
          )}
        </Formik>
      )}
      {!isEditing && (
        <>
          <EditCard
            title={PRIMARY_ADDRESS_HI_COPY.title}
            onEditClick={() => setIsEditing(!isEditing)}
            showEditIcon={true}
            hideBorder={true}
            description={PRIMARY_ADDRESS_HI_COPY.closeCardDescription}
          >
            <SaveAddressList data={data} />
          </EditCard>
          {isError && <Alert message={errorMessage} />}
          <FooterButton
            nextButtonText={
              updateBussinessAddressLoading || props.disableNextStep
                ? LOADING_BUTTON_TEXT
                : NEXT_BUTTON_TEXT
            }
            backButtonText={BACK_BUTTON_TEXT}
            handleNextClick={handleNextClick}
            disabled={props.disableNextStep}
          />
        </>
      )}
    </>
  );
};
export default PrimaryAddressEditForm;
